// src/pages/CampaignPlanPage.js

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'; // Added useRef
import { useParams, useNavigate, Link } from 'react-router-dom'; // Added Link
import {
    doc, getDoc, collection, onSnapshot, addDoc, updateDoc, deleteDoc,
    serverTimestamp, Timestamp, query, orderBy, writeBatch, getDocs, where // Ensure all needed Firestore functions are imported
} from 'firebase/firestore';
import { db } from '../firebase'; // Adjust path if needed
import { useAuth } from '../context/AuthContext'; // Adjust path if needed
import { formatDate } from '../utils/styleUtils'; // Adjust path if needed

// --- React Big Calendar Imports ---
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import enUS from 'date-fns/locale/en-US'; // Or your preferred locale
import 'react-big-calendar/lib/css/react-big-calendar.css'; // Default calendar styles

// --- Local Imports ---
import styles from '../styles/CampaignPlanPage.module.css'; // Your CSS Module path
import EventModal from '../components/EventModal'; // Import the modal component

// Setup React Big Calendar Localization
const locales = { 'en-US': enUS, };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales, });

// --- Helper Function (can be moved to utils) ---
/**
 * Gets an array of Date objects representing weekdays (Mon-Fri) within a range.
 * @param {Date} startDate - The start date (inclusive).
 * @param {Date} endDate - The end date (inclusive).
 * @returns {Date[]} An array of Date objects for each weekday.
 */

const getWeekdaysInRange = (startDate, endDate) => {
    const weekdays = [];
    if (!(startDate instanceof Date) || isNaN(startDate) ||
        !(endDate   instanceof Date) || isNaN(endDate)   ||
        endDate < startDate) {
      console.warn("Invalid dates for getWeekdaysInRange", startDate, endDate);
      return weekdays;
    }
    let cur = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const last = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59);

    while (cur <= last) {
      const dow = cur.getDay();
      // 5 = Friday → exclude it as the only weekend day (Adjust if needed)
      if (dow !== 5) {
        weekdays.push(new Date(cur));
      }
      cur.setDate(cur.getDate() + 1);
    }
    return weekdays;
  };

/**
 * Gets the start of the current day (00:00:00).
 * @returns {Date} Date object representing the start of today.
 */
const getStartOfToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
};

/**
 * Gets the start of tomorrow (00:00:00).
 * @returns {Date} Date object representing the start of tomorrow.
 */
const getStartOfTomorrow = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow;
};

/**
 * Gets the start of the day after tomorrow (00:00:00).
 * @returns {Date} Date object representing the start of the day after tomorrow.
 */
const getStartOfDayAfterTomorrow = () => {
    const dayAfter = new Date();
    dayAfter.setDate(dayAfter.getDate() + 2);
    dayAfter.setHours(0, 0, 0, 0);
    return dayAfter;
};


// Define service types consistently
const serviceTypesForDisplay = [
    { key: 'design', label: 'Design' }, { key: 'reelContentCreation', label: 'Reel (Content)' },
    { key: 'reelPro', label: 'Reel (Pro)' }, { key: 'video', label: 'Video' },
    { key: 'motionGraphics', label: 'Motion' }, { key: 'cgi', label: 'CGI' },
    { key: 'sponsorshipManagement', label: 'Sponsor' },
    { key: 'printsJob', label: 'Prints Job'}
];
// Define roles that should automatically see tasks created by the system/admins
const managerRoles = ['Manager', 'Accounts Manager'];


// --- Component Definition ---
const CampaignPlanPage = () => {
    const { campaignId } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useAuth(); // Assuming useAuth provides currentUser with uid, displayName, email

    // --- Component State ---
    const [campaign, setCampaign] = useState(null);
    const [activities, setActivities] = useState([]);
    const [employees, setEmployees] = useState([]); // Needed for assignees in task creation
    const [managerUids, setManagerUids] = useState([]); // Needed for visibleTo in task creation
    const [loadingCampaign, setLoadingCampaign] = useState(true);
    const [loadingActivities, setLoadingActivities] = useState(true);
    const [loadingEmployees, setLoadingEmployees] = useState(true); // Added
    const [error, setError] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [isDistributing, setIsDistributing] = useState(false);
    const [distributionStrategy, setDistributionStrategy] = useState('roundRobin');
    const [calendarDate, setCalendarDate] = useState(new Date());
    const [calendarView, setCalendarView] = useState('month');
    const [isResetting, setIsResetting] = useState(false);

    // --- Refs ---
    const isMounted = useRef(true); // Track mount status

    // Combined Loading State
    const isLoading = loadingCampaign || loadingActivities || loadingEmployees;

    // --- Effects ---

    // Mount/Unmount Tracking
    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    // Fetch Campaign Details
    useEffect(() => {
        if (!campaignId) return;
        setLoadingCampaign(true); setError('');
        const campaignRef = doc(db, 'campaigns', campaignId);
        const unsubscribeCampaign = onSnapshot(campaignRef, (docSnap) => {
             if (!isMounted.current) return;
             if (docSnap.exists()) {
                const data = docSnap.data();
                const campaignData = { id: docSnap.id, ...data, startDate: data.startDate?.toDate(), endDate: data.endDate?.toDate() };
                setCampaign(campaignData);
                // Set initial calendar date only if campaign data is loaded and has start date
                // This prevents resetting the date on subsequent campaign updates
                if (campaignData.startDate && calendarDate.getTime() === new Date().getTime()) {
                    setCalendarDate(campaignData.startDate);
                }
                setError('');
            } else { setError("Campaign not found."); setCampaign(null); }
             setLoadingCampaign(false);
        }, (err) => { if (isMounted.current) { console.error("Error fetching campaign:", err); setError("Failed campaign details."); setCampaign(null); setLoadingCampaign(false); }});
        return () => unsubscribeCampaign();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignId]); // Dependency: campaignId

    // Fetch/Subscribe to Campaign Activities
    useEffect(() => {
        if (!campaignId) { setLoadingActivities(false); return; };
        setLoadingActivities(true);
        const activitiesRef = collection(db, 'campaigns', campaignId, 'campaignActivities');
        const q = query(activitiesRef, orderBy('start', 'asc'));
        const unsubscribeActivities = onSnapshot(q, (querySnapshot) => {
             if (!isMounted.current) return;
            const fetchedActivities = querySnapshot.docs.map(doc => {
                const data = doc.data();
                return { id: doc.id, ...data, start: data.start?.toDate(), end: data.end?.toDate(), allDay: data.allDay !== undefined ? data.allDay : true, resource: { serviceType: data.serviceType || 'default' }, taskCreated: data.taskCreated || false }; // Include taskCreated
            });
            setActivities(fetchedActivities);
            setLoadingActivities(false);
        }, (err) => { if (isMounted.current) { console.error("Error fetching activities:", err); setError("Failed activities."); setActivities([]); setLoadingActivities(false); }});
        return () => unsubscribeActivities();
    }, [campaignId]);

     // Fetch Employees and Manager UIDs (Runs once)
     useEffect(() => {
        const fetchInitialData = async () => {
            if (!isMounted.current) return;
            setLoadingEmployees(true);
            try {
                const usersCol = collection(db, 'users');
                
                // Fetch All Employees with roles
                const qAll = query(usersCol, orderBy('name', 'asc'));
                const allUsersSnapshot = await getDocs(qAll);
                if (!isMounted.current) return;
                
                // MODIFIED: Include role in employee data
                const employeeList = allUsersSnapshot.docs.map(doc => ({
                    id: doc.id,
                    name: doc.data().name || doc.data().email,
                    role: doc.data().role || 'employee' // Default role if missing
                }));
                setEmployees(employeeList);
    
                // Fetch Managers (existing code remains)
                const qManagers = query(usersCol, where('role', 'in', managerRoles));
                const managerSnapshot = await getDocs(qManagers);
                if (!isMounted.current) return;
                const managerIdList = managerSnapshot.docs.map(doc => doc.id);
                setManagerUids(managerIdList);
    
            } catch (err) {
                console.error("Error fetching employees/managers:", err);
                if (isMounted.current) setError(prev => prev ? `${prev} | Failed users/managers.` : 'Failed users/managers.');
            } finally {
                if (isMounted.current) setLoadingEmployees(false);
            }
        };
        fetchInitialData();
    }, []); // Fetch only once

    // --- Calculate Remaining Service Counts (Memoized) ---
    const remainingServices = useMemo(() => {
        if (!campaign || !campaign.services) return {}; const scheduledCounts = activities.reduce((acc, activity) => { if (activity.serviceType) { acc[activity.serviceType] = (acc[activity.serviceType] || 0) + 1; } return acc; }, {}); const remaining = {}; for (const key in campaign.services) { if (key === 'prints') { const scheduledPrintJobs = scheduledCounts['printsJob'] || 0; remaining['printsCount'] = (campaign.services.prints?.count || 0) - scheduledPrintJobs; } else { remaining[key] = (campaign.services[key] || 0) - (scheduledCounts[key] || 0); } } return remaining;
    }, [campaign, activities]);

    // --- Calendar Event Handlers ---
    const handleSelectSlot = useCallback(({ start, end }) => {
        if (!campaign) return; setSelectedSlot({ start, end }); setSelectedEvent(null); setModalOpen(true); setError('');
    }, [campaign]);

    const handleSelectEvent = useCallback((event) => {
        setSelectedEvent({ ...event, start: event.start instanceof Date ? event.start : new Date(), end: event.end instanceof Date ? event.end : new Date() }); setSelectedSlot(null); setModalOpen(true); setError('');
    }, []);

    // --- Function to Create Task (used by add/update/distribute) ---
    const createTaskForActivity = async (batch, activityId, activityData, campaignData, creatorUid) => {
        if (!activityData.assignedTo || activityData.assignedTo.length === 0) {
            console.log(`Skipping task creation for activity ${activityId} - no assignees.`);
            return; // Don't create task if no one is assigned
        }

         // Construct visibleTo array
         const visibilitySet = new Set();
         if (creatorUid) visibilitySet.add(creatorUid); // Add creator (of the activity/task trigger)
         (activityData.assignedTo || []).forEach(uid => visibilitySet.add(uid)); // Add assignees
         managerUids.forEach(uid => visibilitySet.add(uid)); // Add all managers
         const visibleToArray = Array.from(visibilitySet);

         // Prepare Task Data
         const tasksCol = collection(db, 'tasks');
         const taskStartDate = activityData.start instanceof Timestamp ? activityData.start.toDate() : new Date(activityData.start); // Ensure start is a JS Date
         taskStartDate.setHours(0,0,0,0); // Set task for the whole day

         const newTaskData = {
             taskName: `[Campaign] ${activityData.title || 'Activity'} for ${campaignData.clientName || 'Client'}`,
             description: activityData.notes || `Scheduled activity for campaign: ${campaignData.clientName || campaignId}`,
             startDate: Timestamp.fromDate(taskStartDate),
             endDate: Timestamp.fromDate(taskStartDate), // Single-day task
             status: 'Not Started',
             assignedTo: activityData.assignedTo || [],
             createdBy: creatorUid || 'SYSTEM_AUTO', // Indicate who triggered task creation
             createdAt: serverTimestamp(),
             updatedAt: serverTimestamp(),
             priority: activityData.priority || 'Medium', // Use activity priority or default
             visibleTo: visibleToArray,
             clientId: campaignData.clientId || null,
             clientName: campaignData.clientName || null,
             campaignId: campaignId,
             campaignActivityId: activityId // Link back to activity
         };

         // Add task creation to batch
         const newTaskRef = doc(tasksCol); // Auto-generate ID
         batch.set(newTaskRef, newTaskData);
         console.log(`Task prepared for activity ${activityId}`);

         // Return true to indicate task was prepared
         return true;
    };


    // --- MODIFIED: Add Activity (Includes potential immediate task creation) ---
    const addActivity = useCallback(async (activityData) => {
        if (!campaignId || !currentUser || !campaign) { const msg="Cannot add: Missing context."; setError(msg); throw new Error(msg); }

        const startDate = activityData.start instanceof Date && !isNaN(activityData.start) ? activityData.start : null;
        const endDate = activityData.end instanceof Date && !isNaN(activityData.end) ? activityData.end : null;
        if (!startDate || !endDate || endDate < startDate) { const msg = "Invalid start/end date."; console.error(msg, activityData); setError(msg); throw new Error(msg); }

        setModalOpen(false); setSelectedEvent(null); setSelectedSlot(null); setError('');

        // Prepare data for Firestore activity
        const dataToSave = {
            ...activityData,
            title: activityData.title.trim() || 'Untitled Activity',
            start: Timestamp.fromDate(startDate),
            end: Timestamp.fromDate(endDate),
            assignedTo: activityData.assignedTo || [],
            taskCreated: false // Initialize flag
        };

        let activityRef;
        try {
            const activitiesRef = collection(db, 'campaigns', campaignId, 'campaignActivities');
            activityRef = await addDoc(activitiesRef, dataToSave);
            console.log("Activity added with ID:", activityRef.id);

            // Check if task should be created immediately
            const today = getStartOfToday();
            const dayAfterTomorrow = getStartOfDayAfterTomorrow();
            if (startDate >= today && startDate < dayAfterTomorrow) {
                console.log(`Activity ${activityRef.id} starts soon, creating task...`);
                const batch = writeBatch(db);
                const taskWasPrepared = await createTaskForActivity(batch, activityRef.id, dataToSave, campaign, currentUser.uid);
                if (taskWasPrepared) {
                    batch.update(activityRef, { taskCreated: true }); // Mark activity if task was prepared
                    await batch.commit();
                    console.log(`Task created immediately for activity ${activityRef.id} and flag set.`);
                    // Optional: Add notification logic here if needed for immediate tasks
                } else {
                    console.log(`Task not created for activity ${activityRef.id} (e.g., no assignees).`);
                }
            }

        } catch (err) {
            console.error("Error adding activity or creating immediate task:", err);
            setError("Failed to save activity/task.");
            // Note: No optimistic UI rollback here, relies on listener update
            throw err; // Re-throw
        }
     }, [campaignId, currentUser, campaign, managerUids]); // Added dependencies

     // --- MODIFIED: Update Activity (Includes potential immediate task creation) ---
     const updateActivity = useCallback(async (activityId, activityData) => {
        if (!campaignId || !activityId || !currentUser || !campaign) { setError("Cannot update: Missing context."); throw new Error("Missing context"); }

        const startDate = activityData.start instanceof Date && !isNaN(activityData.start) ? activityData.start : null;
        const endDate = activityData.end instanceof Date && !isNaN(activityData.end) ? activityData.end : null;
        if (!startDate || !endDate || endDate < startDate) { const msg = "Invalid start/end date."; console.error(msg, activityData); setError(msg); throw new Error(msg); }

        setModalOpen(false); setSelectedEvent(null); setSelectedSlot(null); setError('');

        // Prepare data for Firestore update
        const dataToSave = {
            ...activityData,
            title: activityData.title.trim() || 'Untitled Activity',
            start: Timestamp.fromDate(startDate),
            end: Timestamp.fromDate(endDate),
            assignedTo: activityData.assignedTo || [],
            updatedAt: serverTimestamp()
        };

        const activityRef = doc(db, 'campaigns', campaignId, 'campaignActivities', activityId);

        try {
            // Check if task needs immediate creation
            const today = getStartOfToday();
            const dayAfterTomorrow = getStartOfDayAfterTomorrow();
            const shouldCreateTask = startDate >= today && startDate < dayAfterTomorrow;

            // Get the current state of taskCreated before updating
            const originalActivitySnap = await getDoc(activityRef);
            const taskAlreadyCreated = originalActivitySnap.exists() && originalActivitySnap.data().taskCreated === true;

            if (shouldCreateTask && !taskAlreadyCreated) {
                 console.log(`Activity ${activityId} updated to start soon, creating task...`);
                 const batch = writeBatch(db);
                 const taskWasPrepared = await createTaskForActivity(batch, activityId, dataToSave, campaign, currentUser.uid);

                 if (taskWasPrepared) {
                     // Update activity with changes AND set taskCreated flag
                     batch.update(activityRef, { ...dataToSave, taskCreated: true });
                     await batch.commit();
                     console.log(`Task created immediately for updated activity ${activityId} and flag set.`);
                     // Optional notifications
                 } else {
                      // If task wasn't prepared (e.g., no assignees), just update activity
                      await updateDoc(activityRef, dataToSave);
                      console.log("Activity updated (task not created - e.g., no assignees).");
                 }

            } else {
                 // Just update the activity if task doesn't need immediate creation or already exists
                 await updateDoc(activityRef, dataToSave);
                 console.log("Activity updated (no immediate task creation needed/done).");
            }

         } catch (err) {
             console.error("Error updating activity/creating task:", err); setError("Failed to update activity."); throw err;
        }
      }, [campaignId, currentUser, campaign, managerUids]); // Added dependencies

      // Delete Activity
      const deleteActivity = useCallback(async (activityId) => {
        if (!campaignId || !activityId) { setError("Cannot delete: Missing context."); throw new Error("Missing context"); }
         if (window.confirm("Delete this activity? (Associated task, if any, will NOT be deleted automatically)")) {
             setError(''); setModalOpen(false);
             try {
                 const activityRef = doc(db, 'campaigns', campaignId, 'campaignActivities', activityId);
                 await deleteDoc(activityRef); console.log("Activity deleted");
                 // Consider if you need to find and delete the linked task (more complex)
             } catch (err) {
                 console.error("Error deleting activity:", err); setError("Failed delete."); throw err;
             }
         }
       }, [campaignId]);

    // --- Auto-Distribution Handler ---
    const handleDistributeRemaining = useCallback(async () => {
        // ... (Distribution logic - check if assignees need to be added here) ...
        if (!campaign || !campaign.startDate || !campaign.endDate || !currentUser || isDistributing) return;
        const items = []; const labelMap = new Map(serviceTypesForDisplay.map(s => [s.key, s.label]));
        for (const [key, count] of Object.entries(remainingServices)) { if (count > 0) { const serviceKey = key === 'printsCount' ? 'printsJob' : key; const label = labelMap.get(serviceKey) || serviceKey; for (let i = 0; i < count; i++) { items.push({ serviceType: serviceKey, label, instance: i + 1 }); } } }
        if (items.length === 0) return window.alert("No remaining services.");
        let days = getWeekdaysInRange(campaign.startDate, campaign.endDate); if (days.length === 0) { setError("No non-Friday days in range."); return; }
        const names = { roundRobin: 'Round Robin', launchBlitz: 'Launch Blitz', sustainedEngagement:'Sustained Engagement', momentumWave: 'Momentum Wave', strategicPulse: 'Strategic Pulse', closingCrescendo: 'Closing Crescendo' }; const strategyName = names[distributionStrategy] || names.roundRobin;
        if (!window.confirm(`Schedule ${items.length} tasks across ${days.length} days using “${strategyName}”? (Assignees must be added manually later)`)) return; // Inform user about manual assignment
        if (distributionStrategy === 'closingCrescendo') { days = [...days].reverse(); }
        setIsDistributing(true); setError('');
        try {
            const batch = writeBatch(db); const activitiesRef = collection(db, 'campaigns', campaignId, 'campaignActivities'); const D = days.length, M = items.length;
            const today = getStartOfToday();
            const dayAfterTomorrow = getStartOfDayAfterTomorrow();
            const tasksCol = collection(db, 'tasks'); // Ref for tasks

            items.forEach((item, idx) => {
                let date;
                // ... (switch case for distribution strategy remains the same) ...
                 switch (distributionStrategy) { case 'launchBlitz': { const blitzDays = Math.min(3, days.length); date = days[Math.min(idx, blitzDays - 1)]; break; } case 'sustainedEngagement': { const halfPoint = Math.floor(D * 0.4); const engagementDays = days.slice(0, halfPoint).concat(days.slice(-Math.floor(D * 0.6))); date = engagementDays.length > 0 ? engagementDays[idx % engagementDays.length] : days[idx % D]; break; } case 'momentumWave': { const peakPoint = Math.floor(D * 0.6); const phase = idx < M * 0.6 ? Math.floor((idx / (M * 0.6))) * peakPoint : peakPoint + Math.floor(((idx - M * 0.6) / (M * 0.4)) * (D - peakPoint)); date = days[Math.min(phase, D - 1)]; break; } case 'strategicPulse': { const primeDays = days.filter(d => d.getDay() >= 2 && d.getDay() <= 4); date = primeDays.length > 0 ? primeDays[idx % primeDays.length] : days[idx % D]; break; } case 'closingCrescendo': { const finaleDays = days.slice(-3); const intensityMap = [0, 1, 2]; date = finaleDays.length > 0 ? (finaleDays[intensityMap[idx % finaleDays.length]] || finaleDays[finaleDays.length-1]) : days[idx % D]; break; } case 'roundRobin': default: date = days[idx % D]; }

                const activityStartDate = Timestamp.fromDate(date);
                const activityEndDate = Timestamp.fromDate(date);
                const shouldCreateTask = date >= today && date < dayAfterTomorrow; // Check if task needs immediate creation

                const activityData = {
                    title: `${item.label} Task #${item.instance}`, serviceType: item.serviceType,
                    start: activityStartDate, end: activityEndDate, allDay: true,
                    status: "Planned", notes: "Automatically scheduled.",
                    assignedTo: [], // Auto-distributed tasks start unassigned
                    taskCreated: shouldCreateTask // Set flag based on date
                };

                const activityDocRef = doc(activitiesRef); // Get ref for the new activity
                batch.set(activityDocRef, activityData); // Add activity creation to batch

                // If task should be created immediately, add it to the batch
                if (shouldCreateTask) {
                    console.log(`Auto-scheduling activity ${activityDocRef.id} starts soon, creating task...`);
                    // Note: createTaskForActivity requires assignees, which are empty here.
                    // We need to decide: either don't create tasks for auto-distributed items immediately,
                    // or assign them to a default person/group, or modify createTaskForActivity.
                    // For now, we'll log a warning and skip immediate task creation for auto-distributed items.
                    console.warn(`Skipping immediate task creation for auto-scheduled activity ${activityDocRef.id} as it has no assignees.`);
                    // If you want to create tasks anyway (e.g., assigned to creator/manager):
                    // createTaskForActivity(batch, activityDocRef.id, activityData, campaign, currentUser.uid); // Pass relevant data
                }
            });

            await batch.commit();
            console.log(`Distributed ${M} activities (“${strategyName}”). Check activities starting soon for task creation.`);
        } catch (e) { console.error("Distribution error:", e); setError("Failed auto-distribution."); }
        finally { setIsDistributing(false); }
    }, [campaign, remainingServices, distributionStrategy, campaignId, currentUser, isDistributing, managerUids]); // Added managerUids


    // Handle Reset Distribution
    const handleResetDistribution = useCallback(async () => { /* ... same as before ... */
         if (!campaignId || isResetting || isDistributing) return; const ok = window.confirm("RESET distribution? This deletes auto-scheduled tasks."); if (!ok) return; setIsResetting(true); setError(''); try { const activitiesRef = collection(db, 'campaigns', campaignId, 'campaignActivities'); const qSnap = await getDocs(query(activitiesRef, where('notes', '==', 'Automatically scheduled.'))); if (!qSnap.empty) { const batch = writeBatch(db); qSnap.docs.forEach(docSnap => batch.delete(doc(activitiesRef, docSnap.id))); await batch.commit(); console.log(`Deleted ${qSnap.size} auto-scheduled tasks.`); } else { window.alert("No auto-scheduled tasks found."); } } catch (err) { console.error("Reset failed:", err); setError("Failed reset."); } finally { setIsResetting(false); }
       }, [campaignId, isResetting, isDistributing]);

    // --- Event Styling Function ---
    const eventStyleGetter = useCallback((event, start, end, isSelected) => { /* ... same as before ... */
        const serviceType = event.resource?.serviceType || 'default'; const className = `${styles.eventBase} ${styles[`event-${serviceType}`] || styles['event-default']}`; return { className };
    }, []);

    // Calendar Navigation Handlers
    const handleNavigate = useCallback((newDate, view) => { setCalendarDate(newDate); if (view && view !== calendarView) { setCalendarView(view); } }, [calendarView]);
    const handleView = useCallback((newView) => { setCalendarView(newView); }, []);


    // --- Render Logic ---
    if (isLoading) { return <div className={styles.loadingIndicator}>Loading campaign data...</div>; }
    if (!campaign) { return <div className={`${styles.pageContainer} ${styles.errorMessage}`}>{error || 'Campaign data unavailable.'}</div>; }

    return (
        <div className={styles.pageContainer}>
            {/* Campaign Header Info */}
            <h2 className={styles.pageTitle}>Plan Campaign: {campaign.clientName}</h2>
            <p className={styles.campaignDates}> ({formatDate(campaign.startDate)} - {formatDate(campaign.endDate)}) </p>
            <p className={styles.campaignDesc}>{campaign.description}</p>

            {/* Service Pool / Budget Display */}
            <div className={styles.servicePool}>
                <div className={styles.poolHeader}>
                     <h3 className={styles.poolTitle}>Remaining Services Budget</h3>
                     <div className={styles.planControls}>
                         <label htmlFor="distribution-strategy" className={styles.planLabel}> Plan:</label>
                         <select id="distribution-strategy" value={distributionStrategy} onChange={e => setDistributionStrategy(e.target.value)} className={styles.planSelect} disabled={isDistributing || isResetting} >
                            <option value="roundRobin">Cyclic Distribution</option>
                            <option value="launchBlitz">Launch Blitz (First 3 Days)</option>
                            <option value="sustainedEngagement">Sustained Engagement</option>
                            <option value="momentumWave">Momentum Wave</option>
                            <option value="strategicPulse">Strategic Pulse (Tue-Thu)</option>
                            <option value="closingCrescendo">Closing Crescendo</option>
                         </select>
                        <button onClick={handleDistributeRemaining} className={styles.distributeButton} disabled={isLoading || isDistributing || Object.values(remainingServices).every(c => c <= 0)} title={Object.values(remainingServices).every(c => c <= 0) ? "No remaining services" : "Auto-schedule remaining tasks"} > {isDistributing ? <span className={styles.loadingSpinner}></span> : 'Distribute'} </button>
                        <button onClick={handleResetDistribution} className={styles.resetButton} disabled={isLoading || isResetting || isDistributing} title="Remove auto-scheduled tasks" > {isResetting ? <span className={styles.loadingSpinner} /> : 'Reset'} </button>
                    </div>
                 </div>
                 <div className={styles.poolGrid}>
                    {serviceTypesForDisplay.map(service => {
                        const remainingCount = service.key === 'printsJob' ? remainingServices['printsCount'] : remainingServices[service.key];
                        return ( <div key={service.key} className={styles.poolItem}> <span className={styles.poolLabel}>{service.label}:</span> <span className={`${styles.poolCount} ${remainingCount < 0 ? styles.poolCountNegative : ''}`}> {remainingCount ?? 'N/A'} </span> </div> );
                     })}
                     {campaign.services?.prints?.details && ( <div className={`${styles.poolItem} ${styles.poolItemFull}`}> <span className={styles.poolLabel}>Prints Details:</span> <span className={styles.poolDetails}>{campaign.services.prints.details}</span> </div> )}
                </div>
                {Object.entries(remainingServices).some(([key, count]) => (key === 'printsCount' ? count < 0 : (key !== 'prints' && count < 0) )) && <p className={styles.warningMessage}>Warning: Some services scheduled exceed initial plan.</p> }
            </div>

            {/* Display general errors */}
            {error && <div className={styles.errorMessage}>{error}</div>}

            <div className={styles.calendarContainer}>
                {loadingActivities ? ( <div className={styles.loadingIndicator}>Loading activities...</div> )
                : (
                    <Calendar
                        localizer={localizer}
                        events={activities}
                        date={calendarDate}
                        view={calendarView}
                        onNavigate={handleNavigate}
                        onView={handleView}
                        startAccessor="start"
                        endAccessor="end"
                        titleAccessor="title"
                        allDayAccessor="allDay"
                        style={{ height: 650 }}
                        selectable
                        onSelectSlot={handleSelectSlot}
                        onSelectEvent={handleSelectEvent}
                        eventPropGetter={eventStyleGetter}
                        views={['month', 'week', 'day', 'agenda']}
                        min={campaign.startDate}
                        max={campaign.endDate}
                        showMultiDayTimes={false}
                        popup
                    />
                )}
            </div>

            {/* Render Modal Component Conditionally */}
             {modalOpen && (
                <EventModal
                    isOpen={modalOpen}
                    onClose={() => { setModalOpen(false); setSelectedEvent(null); setSelectedSlot(null); setError(''); }}
                    eventData={selectedEvent}
                    slotInfo={selectedSlot}
                    campaignId={campaignId}
                    onSave={selectedEvent ? updateActivity : addActivity}
                    onDelete={deleteActivity}
                    employees={employees} // Pass employees to modal
                />
            )}

        </div>
    );
};

export default CampaignPlanPage;
